/**
 * SpecialOffers Component (San Marino Apartments override)
 *
 * Fixed banner at top of page showing special offer data from collection.
 * Always visible (user can dismiss). Clicking scrolls to contact form.
 * Auto-rotates multiple offers with dot indicators and progress bar.
 *
 * Override note: banner uses the brand secondary color (matching site CTAs)
 * with `secondary-foreground` for contrast-safe text and controls.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
// @ts-ignore
import { CloseIcon } from '@vibesites/assets';

// ============================================================================
// Types & Helpers
// ============================================================================

interface SpecialOffer {
  title?: string | null;
  description?: string | null;
  disclaimer?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  enabled?: string | null;
  image?: string;
  alt_text?: string;
  [key: string]: unknown;
}

function hasValue(v: unknown): v is string {
  if (v == null || v === false) return false;
  const s = String(v).trim().toLowerCase();
  return s !== '' && s !== '-' && s !== 'null' && s !== 'undefined' && s !== 'false';
}

/**
 * Parse an offer start/end date. Empty, "-", "false", "true", and null/undefined
 * are treated as unset and return null. ISO-like strings without a timezone are
 * parsed in the viewer's local time, so an offer ending at 3am on a given date
 * ends at 3am wherever the viewer is.
 */
function parseOfferDate(value: unknown): Date | null {
  if (!hasValue(value)) return null;
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * An offer is active when the current time falls within [start_date, end_date].
 * Missing bounds are treated as open-ended. We intentionally ignore the
 * upstream `enabled` flag because the feed frequently sets it to "false" even
 * when live offer text is present.
 */
function isOfferActive(offer: SpecialOffer | null | undefined, now: Date = new Date()): boolean {
  if (!offer) return false;
  if (!hasValue(offer.title) && !hasValue(offer.description)) return false;
  const start = parseOfferDate(offer.start_date);
  if (start && now < start) return false;
  const end = parseOfferDate(offer.end_date);
  if (end && now > end) return false;
  return true;
}

function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  return html.replace(/<[^>]*>/g, '').trim();
}

function setBannerHeight(px: number) {
  document.documentElement.style.setProperty('--offer-banner-height', `${px}px`);
}

// ============================================================================
// Auto-rotating carousel hook
// ============================================================================

function useOfferRotation(count: number, intervalMs = 6000) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (count <= 1 || paused) return;
    const id = setInterval(() => setActive(i => (i + 1) % count), intervalMs);
    return () => clearInterval(id);
  }, [count, intervalMs, paused]);

  const goTo = useCallback((i: number) => setActive(i), []);
  const pause = useCallback(() => setPaused(true), []);
  const resume = useCallback(() => setPaused(false), []);

  return { active, goTo, pause, resume };
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Special offer background' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_website_url',
      suggestedFields: ['property_website_url', 'property_apply_now_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Learn More' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function SpecialOffers() {
  const title = useContentToken('specialoffers.title');
  const description = useContentToken('specialoffers.description');
  // Hooks register the bindings; the returned values are consumed via the
  // EditableSlotGroup wrappers below, so we don't bind them to locals.
  useSlotGroup('specialoffers.bg_image', bgImageDef);
  useSlotGroup('specialoffers.cta_link', ctaLinkDef);

  const offers = useCollectionBinding<SpecialOffer>('specialoffers.items', {
    collection: 'special_offers',
  });

  const [dismissed, setDismissed] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  const measuredHeight = useRef(0);
  const dismissScrollY = useRef<number | null>(null);
  const hasScrolledAway = useRef(false);

  // Filter to offers that are currently within their start/end window.
  // Offers outside their window are hidden — no content-only fallback, since
  // that would resurrect expired or not-yet-started offers.
  const activeOffers = useMemo(() => {
    return (offers || []).filter((o: SpecialOffer) => isOfferActive(o));
  }, [offers]);

  const hasOffers = activeOffers.length > 0;
  const isVisible = hasOffers && !dismissed;
  const multi = activeOffers.length > 1;

  const { active: activeIdx, goTo, pause, resume } = useOfferRotation(activeOffers.length);

  // Measure banner height and sync CSS variable
  useEffect(() => {
    if (!isVisible || !bannerRef.current) {
      setBannerHeight(0);
      return;
    }
    const updateHeight = () => {
      if (bannerRef.current) {
        measuredHeight.current = bannerRef.current.scrollHeight;
        setBannerHeight(measuredHeight.current);
      }
    };
    updateHeight();
    // Re-measure on resize
    window.addEventListener('resize', updateHeight, { passive: true });
    return () => window.removeEventListener('resize', updateHeight);
  }, [isVisible, activeIdx]);

  // Cleanup on unmount
  useEffect(() => () => setBannerHeight(0), []);

  // Re-show banner when user scrolls back to the very top, but only after
  // they've scrolled away from where they dismissed it first. This prevents
  // the banner from immediately reappearing when dismissed near the top.
  useEffect(() => {
    if (!dismissed || !hasOffers) return;
    hasScrolledAway.current = false;
    const threshold = 150;
    const onScroll = () => {
      const origin = dismissScrollY.current ?? 0;
      if (!hasScrolledAway.current) {
        if (Math.abs(window.scrollY - origin) > threshold) {
          hasScrolledAway.current = true;
        }
        return;
      }
      if (window.scrollY < 10) {
        dismissScrollY.current = null;
        hasScrolledAway.current = false;
        setDismissed(false);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [dismissed, hasOffers]);

  const handleDismiss = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    dismissScrollY.current = window.scrollY;
    hasScrolledAway.current = false;
    setDismissed(true);
  }, []);

  const handleBannerClick = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // If no offers at all, render only hidden linter anchors
  if (!hasOffers) {
    return (
      <section id="special-offers" aria-hidden="true">
        <div className="hidden">
          <EditableToken path="specialoffers.title" as="span">{title}</EditableToken>
          <EditableToken path="specialoffers.description" as="span">{description}</EditableToken>
          <EditableSlotGroup groupPath="specialoffers.bg_image" definition={bgImageDef}><span /></EditableSlotGroup>
          <EditableSlotGroup groupPath="specialoffers.cta_link" definition={ctaLinkDef}><span /></EditableSlotGroup>
        </div>
      </section>
    );
  }

  const offer = activeOffers[activeIdx] || activeOffers[0];
  const offerTitle = hasValue(offer?.title) ? offer!.title! : '';
  const offerDesc = hasValue(offer?.description) ? stripHtml(offer!.description!) : '';
  const offerDisclaimer = hasValue(offer?.disclaimer) ? stripHtml(offer!.disclaimer!) : '';

  return (
    <>
      <section id="special-offers" aria-label="Special offers">
        {/* Fixed banner — always visible until dismissed */}
        <div
          ref={bannerRef}
          className="fixed top-0 left-0 right-0 z-[1100] overflow-hidden cursor-pointer bg-secondary"
          onClick={handleBannerClick}
          style={{
            maxHeight: isVisible ? '140px' : '0px',
            opacity: isVisible ? 1 : 0,
            transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease',
          }}
          onMouseEnter={pause}
          onMouseLeave={resume}
          aria-label="Special offers — click to contact us"
        >
          {/* Shimmer effect */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(110deg, transparent 20%, rgba(255,255,255,0.06) 40%, rgba(255,255,255,0.06) 60%, transparent 80%)',
              backgroundSize: '200% 100%',
              animation: 'specialOfferShimmer 6s ease-in-out infinite',
            }}
          />

          <div className="relative max-w-[1400px] mx-auto px-4 md:px-6 py-2.5 pr-12 flex items-center gap-3">
            {/* Offer content with crossfade */}
            {/*
              All offer copy uses full-opacity `secondary-foreground` (= textOnSecondary)
              rather than hardcoded white or partial opacity. Letting `@vibesites/tokens`
              pick the contrast keeps it readable across every brand secondary.
            */}
            <div className="flex-1 text-center min-w-0">
              <EditableSlotGroup groupPath="specialoffers.cta_link" definition={ctaLinkDef}>
                <div key={activeIdx} style={{ animation: 'specialOfferFadeIn 0.4s ease both' }}>
                  {offerTitle && (
                    <p className="font-heading text-[0.9rem] font-bold text-secondary-foreground tracking-wide m-0 leading-snug">
                      {offerTitle}
                    </p>
                  )}
                  {offerDesc && (
                    <p className="font-body text-[0.8rem] text-secondary-foreground m-0 mt-0.5 leading-snug">
                      {offerDesc}
                    </p>
                  )}
                  {offerDisclaimer && (
                    <p className="font-body text-[0.68rem] italic text-secondary-foreground m-0 mt-0.5 leading-snug">
                      {offerDisclaimer}
                    </p>
                  )}
                </div>
              </EditableSlotGroup>

              {/* Dot indicators */}
              {multi && (
                <div className="flex justify-center gap-1.5 mt-1.5" role="tablist" aria-label="Offers">
                  {activeOffers.map((_, i) => (
                    <button
                      key={i}
                      className={`w-[7px] h-[7px] rounded-full border-[1.5px] p-0 cursor-pointer transition-all duration-200 ${
                        i === activeIdx
                          ? 'bg-secondary-foreground border-secondary-foreground'
                          : 'bg-transparent border-secondary-foreground/50 hover:border-secondary-foreground hover:scale-110'
                      }`}
                      onClick={(e) => { e.stopPropagation(); goTo(i); }}
                      role="tab"
                      aria-selected={i === activeIdx}
                      aria-label={`Offer ${i + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Close button — uses secondary-foreground so the "x" stays visible on any brand secondary */}
            <button
              onClick={handleDismiss}
              className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-[26px] h-[26px] rounded-full border-[1.5px] border-secondary-foreground/25 bg-transparent text-secondary-foreground/70 cursor-pointer transition-all duration-200 hover:bg-secondary-foreground/15 hover:text-secondary-foreground hover:border-secondary-foreground/50"
              aria-label="Dismiss"
            >
              <CloseIcon size={14} />
            </button>
          </div>

          {/* Auto-rotation progress bar */}
          {multi && isVisible && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-secondary-foreground/10">
              <div
                key={activeIdx}
                className="h-full bg-secondary-foreground/40"
                style={{ animation: 'specialOfferProgress 6s linear forwards' }}
              />
            </div>
          )}
        </div>

        {/* Hidden linter anchors for tokens/slots not in main render */}
        <div className="hidden">
          <EditableToken path="specialoffers.title" as="span">{title}</EditableToken>
          <EditableToken path="specialoffers.description" as="span">{description}</EditableToken>
          <EditableSlotGroup groupPath="specialoffers.bg_image" definition={bgImageDef}><span /></EditableSlotGroup>
        </div>
      </section>

      <style>{`
        @keyframes specialOfferShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes specialOfferFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes specialOfferProgress {
          from { width: 0; }
          to { width: 100%; }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="specialOffer"] { animation: none !important; transition: none !important; }
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SpecialOffers',
  displayName: 'Special Offers',
  tokens: {
    'specialoffers.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Current Specials',
    },
    'specialoffers.description': {
      label: 'Description',
      type: 'text',
      defaultValue: 'Take advantage of our current move-in specials and promotions.',
    },
  },
  slotGroups: {
    'specialoffers.bg_image': bgImageDef,
    'specialoffers.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'specialoffers.items': {
      label: 'Special Offers',
      collection: 'special_offers',
    },
  },
};

(SpecialOffers as any).contentSchema = contentSchema;

export default SpecialOffers;
